import { Sprite, Text, Texture } from 'pixi.js';
import gsap from 'gsap';
import { Scene } from '../engine/scene';
import { getContext } from '../engine/context';
import { Button } from '../ui/Button';
import { CoinBalance } from '../ui/CoinBalance';
import { GAME_HEIGHT, GAME_WIDTH, type Side } from '../config/tuning';
import type { Difficulty } from '../config/difficulty';
import type { ScoreSnapshot } from '../systems/ScoreController';
import { HighScoreStore } from '../systems/HighScoreStore';
import { WalletStore } from '../stores/WalletStore';
import { computeCoinReward } from '../config/skins';

interface GameOverData {
  difficulty: Difficulty;
  snapshot: ScoreSnapshot;
  winner: Side | 'tie';
}

/**
 * GameOverScene — banner + stat rows + two action buttons. Layout is anchored
 * to design-space proportions so every element stays balanced on any screen.
 */
export class GameOverScene extends Scene {
  private matchData!: GameOverData;

  async onEnter(data?: unknown): Promise<void> {
    this.matchData = data as GameOverData;
    const ctx = getContext();
    const cx = GAME_WIDTH / 2;

    HighScoreStore.recordMatch(
      this.matchData.difficulty,
      this.matchData.winner === 'player',
      this.matchData.snapshot.totalSeconds.player,
      this.matchData.snapshot.longestSingleDrag.player
    );

    // Coin reward — win-only, based on difficulty + player's total seconds.
    const playerWon = this.matchData.winner === 'player';
    const reward = computeCoinReward(
      this.matchData.difficulty,
      playerWon,
      this.matchData.snapshot.totalSeconds.player,
    );
    if (reward > 0) WalletStore.addCoins(reward);

    // Persistent coin balance (top-right); celebratory +N popup if we won.
    const coinHud = new CoinBalance({
      x: GAME_WIDTH - 180,
      y: 60,
      scale: 0.95,
      fontSize: 32,
      animatePopups: true,
    });
    this.addChild(coinHud);
    if (reward > 0) {
      // Delay slightly so the banner pop lands first.
      setTimeout(() => coinHud.showReward(reward), 650);
    }

    // Banner — top 22% of the canvas.
    const bannerKey =
      this.matchData.winner === 'player'
        ? 'ui_banner_winner'
        : this.matchData.winner === 'ai'
        ? 'ui_banner_loser'
        : 'ui_banner_draw';
    const banner = new Sprite(Texture.from(bannerKey));
    banner.anchor.set(0.5);
    banner.position.set(cx, GAME_HEIGHT * 0.22);
    banner.scale.set(0);
    banner.alpha = 0;
    this.addChild(banner);
    gsap.to(banner, { alpha: 1, duration: 0.6, ease: 'back.out' });
    gsap.to(banner.scale, { x: 0.85, y: 0.85, duration: 0.6, ease: 'back.out' });

    // Stat rows — middle band.
    const stats = this.buildStatRows();
    const statsTopY = GAME_HEIGHT * 0.42;
    const rowSpacing = 54;
    const labelOffset = 200;

    stats.forEach((row, i) => {
      const y = statsTopY + i * rowSpacing;
      const labelText = new Text({
        text: row.label,
        style: { fontFamily: 'Inter, sans-serif', fontSize: 24, fill: 0x88e8db },
      });
      labelText.anchor.set(1, 0.5);
      labelText.position.set(cx - labelOffset, y);
      this.addChild(labelText);

      const valueText = new Text({
        text: row.value,
        style: { fontFamily: 'Impact, sans-serif', fontSize: 28, fill: 0xffd23f },
      });
      valueText.anchor.set(1, 0.5);
      valueText.position.set(cx + labelOffset, y);
      this.addChild(valueText);
    });

    // Action buttons — bottom.
    const btnY = GAME_HEIGHT - 160;
    const btnOffsetX = 210;
    const btnScale = 0.82;

    const replayBtn = new Button({
      x: cx - btnOffsetX,
      y: btnY,
      idleKey: 'ui_btn_replay',
      hoverKey: 'ui_btn_replay_hover',
      onClick: () =>
        ctx.sceneManager.start('GameScene', { difficulty: this.matchData.difficulty }),
      audio: ctx.audio,
    });
    replayBtn.scale.set(btnScale);
    this.addChild(replayBtn);

    const menuBtn = new Button({
      x: cx + btnOffsetX,
      y: btnY,
      idleKey: 'ui_btn_menu',
      hoverKey: 'ui_btn_menu_hover',
      onClick: () => ctx.sceneManager.start('MenuScene'),
      audio: ctx.audio,
    });
    menuBtn.scale.set(btnScale);
    this.addChild(menuBtn);

    // High-scores link
    const hs = new Text({
      text: 'View High Scores',
      style: {
        fontFamily: 'Impact, sans-serif',
        fontSize: 22,
        fill: 0x5fdbcf,
      },
    });
    hs.anchor.set(0.5);
    hs.position.set(cx, GAME_HEIGHT - 50);
    hs.eventMode = 'static';
    hs.cursor = 'pointer';
    hs.on('pointerover', () => { hs.style.fill = 0xffd23f; });
    hs.on('pointerout', () => { hs.style.fill = 0x5fdbcf; });
    hs.on('pointerdown', () => {
      ctx.audio.play('sfx_click', { volume: 0.55 });
      ctx.sceneManager.start('HighScoreScene', {
        from: 'GameOverScene',
        payload: this.matchData,
      });
    });
    this.addChild(hs);
  }

  private buildStatRows(): Array<{ label: string; value: string }> {
    const s = this.matchData.snapshot;
    return [
      { label: 'Your total drag time', value: `${s.totalSeconds.player.toFixed(2)}s` },
      { label: 'Bot total drag time', value: `${s.totalSeconds.ai.toFixed(2)}s` },
      { label: 'Your longest single drag', value: `${s.longestSingleDrag.player.toFixed(2)}s` },
      { label: 'Bot longest single drag', value: `${s.longestSingleDrag.ai.toFixed(2)}s` },
      { label: 'Difficulty', value: this.matchData.difficulty.toUpperCase() },
    ];
  }
}
